/**
 * QR 코드 검증 및 테이블 세션 API
 *
 * @see Issue #208 - QR 스캔 기능 구현
 */

import { apiClient, validatePathParam, ApiClientError } from './client';
import type {
  TableSessionResponse,
  TableSessionExtendResponse,
  TableSessionEndResponse,
} from './types';
import type { BrowserCoordinates } from '@/lib/geolocation';

// === 타입 정의 ===

/** BE TableSessionStatus enum과 일치 */
type TableSessionStatusValue = 'active' | 'completed' | 'expired' | 'cancelled';

interface QrStoreInfo {
  id: number;
  name: string;
  slug: string;
  nickname: string | null;
  address?: string | null;
  /** pickup QR에서만 필수. view QR은 미포함 가능 */
  pickup_available?: boolean;
  /** #271: BE가 lat/lng를 응답에 포함할 경우 위치 앵커로 활용 */
  latitude?: number | null;
  longitude?: number | null;
}

interface QrTableInfo {
  id: number;
  table_number: string;
  table_name: string | null;
  display_name: string;
  capacity: number;
  store: {
    id: number;
    name: string;
    slug: string;
    nickname: string | null;
    /** #271: BE가 lat/lng를 응답에 포함할 경우 위치 앵커로 활용 */
    latitude?: number | null;
    longitude?: number | null;
  };
}

export interface QrActiveSession {
  id: number;
  session_token?: string;
  guest_count: number;
  split_payment_available: boolean;
  has_split_ledger: boolean;
  started_at: string;
  expires_at: string;
  status: TableSessionStatusValue;
  /** BE TableSessionResource 가 제공하는 경과 시간(분) — 점유 안내 화면 요약용 (#674) */
  duration_minutes?: number;
}

export interface QrTableVerifyResponse {
  data: {
    type: 'table';
    valid: true;
    table: QrTableInfo;
    has_active_session: boolean;
    can_start_session: boolean;
    /** 빈 세션 시작 시에만 적용되는 매장 위치 게이트 정책. 매장 좌표는 포함하지 않는다. */
    location_gate?: {
      required: boolean;
      radius_meters?: number | null;
    };
    active_session: QrActiveSession | null;
    /** split ON 활성 세션에 view-only로 붙는 짧은 bootstrap entry. */
    entry_token?: string | null;
    entry_expires_in?: number | null;
  };
}

export interface QrPickupVerifyResponse {
  data: {
    type: 'pickup';
    valid: true;
    store: QrStoreInfo;
  };
}

export interface QrViewVerifyResponse {
  data: {
    type: 'view';
    valid: true;
    store: QrStoreInfo;
  };
}

export type QrVerifyResponse = QrTableVerifyResponse | QrPickupVerifyResponse | QrViewVerifyResponse;

/** 부분 타입: FE에서 사용하는 필드만 정의 (BE TableSessionResource 참조) */
export interface TableSessionStartResponse {
  data: {
    id: number;
    session_token: string;
    guest_count: number;
    started_at: string;
    expires_at: string;
    status: TableSessionStatusValue;
    split_payment_available: boolean;
    has_split_ledger: boolean;
    join_strategy?: 'code' | 'host_approval' | null;
    participants?: { joined_count: number };
    table: QrTableInfo;
  };
  message: string;
}

// === 점유 테이블 판별 (#674) ===

/**
 * 점유 테이블 에러로 인정하는 error code 목록.
 *
 * BE 는 현재 이 엔드포인트에 error_code 를 보내지 않지만 (message + data 만),
 * admin #1854 가 error_code envelope 를 도입했으므로 forward-compat 으로 정의.
 * code 가 명시됐는데 이 목록에 없으면 점유로 간주하지 않는다 (fail-safe).
 */
const TABLE_OCCUPIED_ERROR_CODES: readonly string[] = [
  'TABLE_SESSION_ALREADY_ACTIVE',
  'TABLE_ALREADY_OCCUPIED',
];

/** ISO 8601 등 Date.parse 가능한 날짜 문자열인지 검증 */
function isParseableDateString(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

/**
 * 에러 본문의 `data.active_session` 을 엄격 스키마 검증 후 추출 (codex I2).
 *
 * 유사/위조 본문이 점유 화면으로 오인되지 않도록 필드 의미 수준까지 검증:
 * - id / guest_count: number
 * - status: 'active' enum 엄격 (점유 = 활성 세션만 의미 있음)
 * - started_at / expires_at: Date.parse 가능한 날짜 문자열
 * - duration_minutes: 키 존재 시 유한·비음수 number 만 인정 (위반 시 전체 거부)
 */
function parseActiveSessionFromErrorBody(body: unknown): QrActiveSession | null {
  if (typeof body !== 'object' || body === null) return null;

  const data = (body as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return null;

  const session = (data as { active_session?: unknown }).active_session;
  if (typeof session !== 'object' || session === null) return null;

  const candidate = session as Record<string, unknown>;
  if (
    typeof candidate.id !== 'number' ||
    typeof candidate.guest_count !== 'number' ||
    typeof candidate.split_payment_available !== 'boolean' ||
    typeof candidate.has_split_ledger !== 'boolean' ||
    !isParseableDateString(candidate.started_at) ||
    !isParseableDateString(candidate.expires_at) ||
    candidate.status !== 'active'
  ) {
    return null;
  }

  // duration_minutes 는 선택 필드지만, 존재하면 유한·비음수여야 함 (위조 본문 거부)
  let durationMinutes: number | undefined;
  if (candidate.duration_minutes !== undefined && candidate.duration_minutes !== null) {
    if (
      typeof candidate.duration_minutes !== 'number' ||
      !Number.isFinite(candidate.duration_minutes) ||
      candidate.duration_minutes < 0
    ) {
      return null;
    }
    durationMinutes = candidate.duration_minutes;
  }

  return {
    id: candidate.id,
    session_token: typeof candidate.session_token === 'string' ? candidate.session_token : undefined,
    guest_count: candidate.guest_count,
    split_payment_available: candidate.split_payment_available,
    has_split_ledger: candidate.has_split_ledger,
    started_at: candidate.started_at,
    expires_at: candidate.expires_at,
    status: 'active',
    duration_minutes: durationMinutes,
  };
}

/**
 * "이미 활성화된 세션이 있는 테이블" 에러에서 active_session payload 추출 (#674).
 *
 * BE POST /api/v1/qr/{qrCode}/session 은 점유 시 409 +
 * `{ message, data: { active_session: {...} } }` 를 반환하지만, client.ts 가
 * 한국어 server message 를 generic fallback 으로 대체하면서 구조화 데이터가
 * 뭉개졌었다. 판별은 fail-safe:
 *
 * 0. HTTP status 409 (Conflict) 가 아니면 즉시 거부 (codex I1 — 다른 엔드포인트/
 *    위조 에러 본문에 active_session 유사 구조가 있어도 점유 화면 오인 차단)
 * 1. error_code 가 명시된 경우 — 점유 코드일 때만 인정 (admin #1854 envelope 정합)
 * 2. code 부재 시 — body.data.active_session 엄격 스키마 검증으로 판별
 * 3. 그 외 — null 반환 → 호출 측은 기존 message / generic 분기 유지
 *
 * @returns 점유 테이블 에러면 active_session, 아니면 null
 */
export function extractOccupiedTableSession(error: unknown): QrActiveSession | null {
  if (!(error instanceof ApiClientError)) return null;

  // 점유 응답은 409 Conflict 한정 — 다른 status 의 유사 본문은 점유로 간주하지 않음
  if (error.status !== 409) return null;

  const session = parseActiveSessionFromErrorBody(error.body);
  if (session === null) return null;

  // code 가 명시됐는데 점유 코드가 아니면 다른 에러 — 점유 화면으로 오인하지 않음
  if (error.code !== undefined && !TABLE_OCCUPIED_ERROR_CODES.includes(error.code)) {
    return null;
  }

  return session;
}

// === API 함수 ===

/**
 * QR 코드 검증
 * GET /api/v1/qr/{qrCode}
 */
export async function verifyQrCode(qrCode: string): Promise<QrVerifyResponse> {
  const safeCode = validatePathParam(qrCode, 'qrCode');
  return apiClient.get<QrVerifyResponse>(`/api/v1/qr/${safeCode}`);
}

/**
 * 테이블 세션 시작
 * POST /api/v1/qr/{qrCode}/session
 */
export async function startTableSession(
  qrCode: string,
  guestCount: number = 1,
  coordinates: BrowserCoordinates | null = null,
): Promise<TableSessionStartResponse> {
  const safeCode = validatePathParam(qrCode, 'qrCode');
  const body: Record<string, number | string> = { guest_count: guestCount };

  if (coordinates !== null) {
    body.lat = coordinates.lat;
    body.lng = coordinates.lng;
    body.captured_at = coordinates.capturedAt;
  }

  return apiClient.post<TableSessionStartResponse>(
    `/api/v1/qr/${safeCode}/session`,
    body,
  );
}

// === 세션 관리 API (#220, #221) ===

/**
 * 세션 조회
 * GET /api/v1/sessions/{sessionToken}
 */
export async function getSession(
  token: string,
  signal?: AbortSignal,
): Promise<TableSessionResponse> {
  const safeToken = validatePathParam(token, 'sessionToken');
  const endpoint = `/api/v1/sessions/${safeToken}`;
  return signal
    ? apiClient.get<TableSessionResponse>(endpoint, { signal })
    : apiClient.get<TableSessionResponse>(endpoint);
}

/**
 * 세션 연장 (30분)
 * POST /api/v1/sessions/{sessionToken}/extend
 */
export async function extendSession(
  token: string,
  signal?: AbortSignal,
): Promise<TableSessionExtendResponse> {
  const safeToken = validatePathParam(token, 'sessionToken');
  const endpoint = `/api/v1/sessions/${safeToken}/extend`;
  return signal
    ? apiClient.post<TableSessionExtendResponse>(endpoint, undefined, { signal })
    : apiClient.post<TableSessionExtendResponse>(endpoint);
}

/**
 * 세션 종료
 * POST /api/v1/sessions/{sessionToken}/end
 */
export async function endSession(
  token: string,
  signal?: AbortSignal,
): Promise<TableSessionEndResponse> {
  const safeToken = validatePathParam(token, 'sessionToken');
  const endpoint = `/api/v1/sessions/${safeToken}/end`;
  return signal
    ? apiClient.post<TableSessionEndResponse>(endpoint, undefined, { signal })
    : apiClient.post<TableSessionEndResponse>(endpoint);
}
